/**
 * Analytics Service for ClubIQ
 * 
 * Provides a unified interface for tracking user interactions and events.
 * Currently integrates with Google Analytics 4 (gtag).
 * 
 * To enable analytics:
 * 1. Replace GA_MEASUREMENT_ID in public/index.html with your actual GA4 Measurement ID
 * 2. Import and use this service throughout the app
 */

// Declare gtag on window
declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'set',
      eventName: string,
      params?: Record<string, unknown>
    ) => void;
  }
}

// Check if analytics is available
const isAnalyticsAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Track a generic event
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  if (isAnalyticsAvailable()) {
    window.gtag?.('event', eventName, params);
  }
};

// Track page views (for SPA navigation)
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  if (isAnalyticsAvailable()) {
    window.gtag?.('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle || document.title,
      page_location: window.location.href
    });
  }
};

// ============================================
// GOLF-SPECIFIC EVENTS
// ============================================

// User completed onboarding
export const trackOnboardingComplete = (data: {
  handicap?: number;
  skillLevel?: string;
  sevenIronDistance?: number;
}): void => {
  trackEvent('onboarding_complete', {
    event_category: 'engagement',
    handicap: data.handicap,
    skill_level: data.skillLevel,
    seven_iron_distance: data.sevenIronDistance
  });
};

// User added a club to their bag
export const trackClubAdded = (clubType: string, brand?: string, model?: string): void => {
  trackEvent('club_added', {
    event_category: 'bag_management',
    club_type: clubType,
    brand: brand,
    model: model
  });
};

// User ran bag analysis
export const trackBagAnalysis = (bagScore: number, problemCount: number): void => {
  trackEvent('bag_analysis_run', {
    event_category: 'analysis',
    bag_score: bagScore,
    problem_count: problemCount
  });
};

// User viewed a recommendation
export const trackRecommendationViewed = (
  problemType: string,
  recommendedClubType: string
): void => {
  trackEvent('recommendation_viewed', {
    event_category: 'recommendations',
    problem_type: problemType,
    recommended_club_type: recommendedClubType
  });
};

// User clicked on a recommendation (buy link)
export const trackRecommendationClick = (
  clubBrand: string,
  clubModel: string,
  priceRange: string,
  destination: string
): void => {
  trackEvent('recommendation_click', {
    event_category: 'recommendations',
    club_brand: clubBrand,
    club_model: clubModel,
    price_range: priceRange,
    destination: destination
  });
};

// User used the AI chat
export const trackAIChatMessage = (messageType: 'question' | 'response'): void => {
  trackEvent('ai_chat_message', {
    event_category: 'ai_features',
    message_type: messageType
  });
};

// User used distance calculator
export const trackDistanceCalculation = (clubType: string): void => {
  trackEvent('distance_calculated', {
    event_category: 'tools',
    club_type: clubType
  });
};

// User used shot advisor
export const trackShotAdvice = (distance: number, situation: string): void => {
  trackEvent('shot_advice_requested', {
    event_category: 'tools',
    distance: distance,
    situation: situation
  });
};

// User shared their bag
export const trackBagShared = (platform: string): void => {
  trackEvent('bag_shared', {
    event_category: 'social',
    platform: platform
  });
};

// User signed up
export const trackSignUp = (method: string): void => {
  trackEvent('sign_up', {
    event_category: 'auth',
    method: method
  });
};

// User signed in
export const trackSignIn = (method: string): void => {
  trackEvent('login', {
    event_category: 'auth',
    method: method
  });
};

// Error occurred
export const trackError = (errorType: string, errorMessage: string): void => {
  trackEvent('error', {
    event_category: 'errors',
    error_type: errorType,
    error_message: errorMessage
  });
};

// User upgraded to premium
export const trackPremiumUpgrade = (plan: string): void => {
  trackEvent('premium_upgrade', {
    event_category: 'monetization',
    plan: plan
  });
};

// User clicked affiliate link
export const trackAffiliateClick = (
  retailer: string,
  productType: string,
  productName: string
): void => {
  trackEvent('affiliate_click', {
    event_category: 'monetization',
    retailer: retailer,
    product_type: productType,
    product_name: productName
  });
};

// Feature engagement tracking
export const trackFeatureEngagement = (featureName: string, action: string): void => {
  trackEvent('feature_engagement', {
    event_category: 'engagement',
    feature_name: featureName,
    action: action
  });
};

// Export all as default
const analyticsExports = {
  trackEvent,
  trackPageView,
  trackOnboardingComplete,
  trackClubAdded,
  trackBagAnalysis,
  trackRecommendationViewed,
  trackRecommendationClick,
  trackAIChatMessage,
  trackDistanceCalculation,
  trackShotAdvice,
  trackBagShared,
  trackSignUp,
  trackSignIn,
  trackError,
  trackPremiumUpgrade,
  trackAffiliateClick,
  trackFeatureEngagement
};

export default analyticsExports;
